import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, CheckCircle2, Eye, Sparkles, Volume2 } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import { Button } from '../../components/ui/button'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useEnglishReview } from '../../hooks/useEnglishReview'
import { useTextToSpeech } from '../../hooks/useTextToSpeech'
import { RATING } from '../../lib/srs'
import { cn } from '../../lib/utils'

// Rating buttons sit beneath the "Show answer" reveal so the user
// only sees them once they've committed to a guess. Tone matches the
// recall difficulty: red for Again, amber for Hard, primary for Good,
// emerald for Easy.
const RATING_BUTTONS = [
  { rating: RATING.AGAIN, key: 'again', tone: 'bg-red-500/10 text-red-500 border-red-500/40 hover:bg-red-500/20' },
  { rating: RATING.HARD,  key: 'hard',  tone: 'bg-amber-500/10 text-amber-600 border-amber-500/40 hover:bg-amber-500/20' },
  { rating: RATING.GOOD,  key: 'good',  tone: 'bg-app-primary-500/10 text-app-primary-500 border-app-primary-500/40 hover:bg-app-primary-500/20' },
  { rating: RATING.EASY,  key: 'easy',  tone: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/20' },
]

function Flashcard({ card, revealed, onReveal, onSpeak }) {
  const { t } = useTranslation()
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4 sm:p-6 space-y-4 min-h-[320px] flex flex-col">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap" dir="ltr">
              <h2 className="text-2xl font-bold text-foreground">{card.word}</h2>
              {card.pronunciation_ipa && (
                <span className="text-sm text-muted-foreground font-mono">{card.pronunciation_ipa}</span>
              )}
            </div>
            {card.part_of_speech && (
              <p className="text-[11px] uppercase tracking-wide text-app-primary-500 font-semibold mt-1" dir="ltr">
                {card.part_of_speech}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onSpeak(card.word)}
            aria-label={t('english.review.speak')}
            className="p-2 rounded-md text-muted-foreground hover:text-app-primary-500 hover:bg-accent transition-colors flex-shrink-0"
          >
            <Volume2 className="w-5 h-5" />
          </button>
        </div>

        {card.context_sentence && (
          <div
            className="text-sm text-muted-foreground italic border-s-2 border-app-primary-500/30 ps-2 leading-relaxed"
            dir="auto"
          >
            &ldquo;{card.context_sentence}&rdquo;
          </div>
        )}

        <div className="flex-1" />

        {!revealed ? (
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onReveal}
            className="w-full"
          >
            <Eye className="w-4 h-4 me-2" />
            {t('english.review.reveal')}
          </Button>
        ) : (
          <div className="space-y-2 border-t border-border pt-3">
            {card.definition_en && (
              <p className="text-sm text-foreground leading-relaxed" dir="ltr">
                {card.definition_en}
              </p>
            )}
            {card.definition_ar && (
              <p className="text-sm text-foreground leading-relaxed" dir="rtl">
                {card.definition_ar}
              </p>
            )}
            {Array.isArray(card.examples) && card.examples.length > 0 && (
              <ul className="text-xs text-muted-foreground space-y-1 ps-3 list-disc list-outside" dir="ltr">
                {card.examples.slice(0, 3).map((ex, i) => (
                  <li key={i}>{ex}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function EnglishReviewPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  // Owner gate.
  useEffect(() => {
    if (authLoading) return
    if (!user || !hasOwnerAccess(user)) navigate('/', { replace: true })
  }, [user, authLoading, navigate])

  const { current, remaining, done, loading, rate } = useEnglishReview()
  const tts = useTextToSpeech()
  const [revealed, setRevealed] = useState(false)

  // Reset reveal state on each new card.
  useEffect(() => {
    setRevealed(false)
  }, [current?.id])

  const handleSpeak = (word) => {
    if (!tts.supported || !word) return
    tts.play(word, 'en')
  }
  const handleRate = (rating) => {
    if (!revealed) return
    rate(rating)
  }

  const goBack = () => navigate('/english')

  return (
    <div className="space-y-3 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={goBack}
          aria-label={t('settings.back')}
          className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
        >
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-foreground flex items-center gap-1.5" dir="auto">
            <Sparkles className="w-4 h-4 text-app-primary-500" />
            {t('english.review.title')}
          </h1>
          <p className="text-xs text-muted-foreground" dir="auto">
            {t('english.review.progress', { done, remaining })}
          </p>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-80 w-full" />
      ) : !current ? (
        done > 0 ? (
          <Card>
            <CardContent className="p-6 space-y-3 text-center">
              <div className="inline-flex p-3 rounded-full bg-emerald-500/10 text-emerald-500">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h2 className="text-lg font-bold text-foreground" dir="auto">
                {t('english.review.doneTitle')}
              </h2>
              <p className="text-sm text-muted-foreground" dir="auto">
                {t('english.review.doneBody', { count: done })}
              </p>
              <div className="flex gap-2 justify-center pt-2">
                <Button variant="outline" onClick={goBack}>
                  {t('english.review.backToDashboard')}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <EmptyState
            icon={Sparkles}
            title={t('english.review.empty')}
            description={t('english.review.emptyHint')}
          />
        )
      ) : (
        <>
          <Flashcard
            card={current}
            revealed={revealed}
            onReveal={() => setRevealed(true)}
            onSpeak={handleSpeak}
          />

          {revealed && (
            <div className="grid grid-cols-4 gap-2">
              {RATING_BUTTONS.map((b) => (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => handleRate(b.rating)}
                  className={cn(
                    'rounded-md border px-2 py-3 text-xs font-semibold transition-colors',
                    b.tone,
                  )}
                >
                  {t(`english.review.rate.${b.key}`)}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
